import React, { useState } from 'react'

import { ETaskStatus } from 'types/GlobalTask'
import { TTask } from 'redux/hooks/Tasks'
import { SxProps } from '@mui/material'

import { useTaskActions } from 'redux/actions/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import InputBase from 'components_new/atoms/InputBase'

import ContentBox from 'components_new/molecules/ContentBox'

import SelectUser from 'components_new/organisms/SelectUser'
import TaskStatusChip from 'components_new/organisms/tasks/TaskStatusChip'

import DeadlineField from './DeadlineField'

interface AddSubtaskItemProps {
  onClose: () => void
  open: boolean
  parentId: string
  sx?: SxProps
}

const DEFAULT_DATA = {
  deadline_date: null,
  responsible_account_id: null,
  status: ETaskStatus.TODO,
  title: ''
}

const AddSubtaskItem = (props: AddSubtaskItemProps) => {
  const { onClose, open, parentId, sx } = props

  const { t } = useTranslation()
  const { tryCreateTask } = useTaskActions()

  const [data, setData] = useState<{
    deadline_date: string | null
    responsible_account_id: TTask['responsible_account_id']
    status: TTask['status']
    title: string
  }>(DEFAULT_DATA)
  const [loading, setLoading] = useState<boolean>(false)

  const title = data.title.trim()

  const handleSubmit = () => {
    if (!title || loading) return

    setLoading(true)

    tryCreateTask({ ...data, parent_task_id: parentId, title }, (err) => {
      setLoading(false)

      if (!err) {
        handleClose()
      }
    })
  }

  const handleCancel = () => {
    handleClose()
  }

  const handleClose = () => {
    setData(DEFAULT_DATA)
    onClose()
  }

  const handleChangeTitle = (value: string) => {
    setData((prevState) => ({
      ...prevState,
      title: value
    }))
  }

  const handleChangeOwner = (value: TTask['responsible_account_id']) => {
    setData((prevState) => ({
      ...prevState,
      responsible_account_id: value
    }))
  }

  const handleChangeDeadline = (value: string | null) => {
    setData((prevState) => ({
      ...prevState,
      deadline_date: value
    }))
  }

  const handleChangeStatus = (status: TTask['status']) => {
    setData((prevState) => ({ ...prevState, status }))
  }

  return (
    <Collapse in={open} unmountOnExit={true}>
      <ContentBox
        border={true}
        footer={
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
              <TaskStatusChip
                onChange={handleChangeStatus}
                size="small"
                value={data.status}
              />

              <SelectUser
                key="owner"
                onChange={(value) => handleChangeOwner(value)}
                size="small"
                value={data.responsible_account_id}
              />

              <DeadlineField
                onChange={handleChangeDeadline}
                status={data.status}
                value={data.deadline_date}
              />
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
              <Button onClick={handleCancel} size="small" variant="outlined">
                {t('global', 'cancel')}
              </Button>
              <Button
                disabled={!title}
                loading={loading}
                onClick={handleSubmit}
                size="small"
              >
                {t('global', 'create')}
              </Button>
            </Box>
          </Box>
        }
        sx={sx}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <Icon color="disabled" fontSize="small" name="Task" />

          <InputBase
            autoFocus={true}
            fullWidth={true}
            inputProps={{ autocomplete: 'off' }}
            margin="none"
            onChange={(event) => handleChangeTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit()
              } else if (event.key === 'Escape') {
                handleCancel()
              }
            }}
            placeholder={t('tasks', 'subtask_placeholder')}
            value={data.title}
          />
        </Box>
      </ContentBox>
    </Collapse>
  )
}

export default AddSubtaskItem
